"use client";

import { useState } from "react";

import {
  AUTONOMY_PRACTITIONER_NPIS,
  buildTeReasonContext,
  generateTeMessage,
  generateTeReasonText,
  getMissingInformationSummary,
  hasAutonomyDiagnosisRules,
  hasSleepDiagnosis,
  isOptumServeReferral,
  requiresHmoReferral,
} from "@/configuration/groups/autonomy/configuration";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { PDFViewer } from "@/src/components/pdf-viewer/pdf-viewer";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/shadcn/ui/dialog";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { formatDateForFilename } from "@/src/lib/utils/date-and-phone-utils";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskSource } from "@/src/models/task";

const AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID: Record<string, string> = Object.fromEntries(
  Object.entries(AUTONOMY_PRACTITIONER_NPIS).map(([npi, info]) => [info.ehrId, npi])
);

interface AutonomyReferralInstructionsProps {
  referral: Referral;
  task: Task;
  faxSenderNumber?: string | null;
}

const AUTONOMY_ECW_URL = "https://gacent96bbda5pqn16app.ecwcloud.com/mobiledoc/jsp/webemr/index.jsp";

function findOutboundFaxFile(task: Task) {
  return task.files.find((file) => file.source === TaskSource.FAX_OUTBOUND);
}

function getProviderName(provider: Practitioner | null, fallback: string): string {
  if (provider) return getPractitionerNameWithCredentials(provider) || fallback;
  else return fallback;
}

function AssignmentNote() {
  return (
    <div className="text-xs text-muted-foreground italic">
      Note: Verify assignment based on patient&apos;s last encounter provider
    </div>
  );
}

function AutonomyReferralMessage({ referral, task, faxSenderNumber = null }: AutonomyReferralInstructionsProps) {
  const teContext = buildTeReasonContext(referral, task, null);
  const teReason = generateTeReasonText(teContext);
  const teMessage = generateTeMessage(referral, task, faxSenderNumber);
  const missingInfo = getMissingInformationSummary(referral, task);
  const needsHmoReferral = requiresHmoReferral(referral);
  const isUrgent = isStatOrUrgentPriority(referral.schedulingPriority);
  const isSleepDiagnosis = hasSleepDiagnosis(referral);
  const isOptumServe = isOptumServeReferral(faxSenderNumber, referral);

  const { data: matchedProviders } = useProvidersForDiagnostics(referral.diagnosisList ?? null);

  const outboundFaxFile = findOutboundFaxFile(task);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fileName = `${formatDateForFilename(referral.dateReceived, "")} Referral from ${getProviderName(referral.referringProvider, "Unknown Provider")}`;

  const renderingProviderName = getProviderName(referral.receivingProvider, "Rendering Provider");

  const npi = referral.receivingProvider?.npiNumber ?? null;
  const supervisorLeadMa = (npi ? AUTONOMY_PRACTITIONER_MAPPINGS[npi] : null)?.supervisorLeadMa || null;
  const staffName = isSleepDiagnosis ? "Nicole Dunlap" : supervisorLeadMa;

  const matchedNpis = new Set(
    (matchedProviders ?? [])
      .map((p) => AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID[p.ehrId])
      .filter((matched): matched is string => !!matched)
  );
  const hasProviderConflict =
    !isSleepDiagnosis && hasAutonomyDiagnosisRules(npi) && !!matchedProviders?.length && !matchedNpis.has(npi);
  const alternativeProviderNames = (matchedProviders ?? []).map((p) => p.name);

  return (
    <div className="flex flex-col gap-1">
      <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>

      <div className="text-muted-foreground flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <span className="font-semibold">
            1. Create or Update the patient in eCW:{" "}
            <a
              href={AUTONOMY_ECW_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:bg-accent rounded p-1"
            >
              Link
            </a>
          </span>
          <div className="ml-4 space-y-1 text-xs">
            <div>Complete: Pharmacy, Additional Information, Responsible Party sections</div>
            <div>Run insurance eligibility if plan type is unclear</div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold">2. Upload the file to the proper folder</span>
          <div className="ml-4 space-y-2">
            <div>
              <span className="font-medium">Document Type:</span> <CopyableContent text="Patient Referral" />
            </div>
            <div>
              <span className="font-medium">ECW Folder:</span> <CopyableContent text="Outside Records->Referrals" />
            </div>
            <div>
              <span className="font-medium">File name:</span> <CopyableContent text={fileName} />
            </div>
            <div className="space-y-1">
              <div>
                <span className="font-medium">Assign to (receiving provider):</span>{" "}
                <CopyableContent text={staffName ?? `${renderingProviderName} - Staff`} />
              </div>
              <AssignmentNote />
            </div>
            <div className="text-xs text-muted-foreground italic">
              BCBS referrals: leave document open. All other insurance: mark as reviewed.
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="font-semibold">3. Telephone Encounter</span>

          <div className="ml-4 space-y-3">
            <div>
              <div className="font-medium">Assignment (receiving provider)</div>
              <div className="ml-2 text-sm space-y-1">
                <div>
                  <CopyableContent text={staffName ?? `${renderingProviderName} - Staff`} />
                </div>
                <AssignmentNote />
              </div>
            </div>

            <div>
              <div className="font-medium">Reason</div>
              <div className="ml-2 bg-muted/50 p-2 rounded">
                <CopyableContent text={teReason} />
              </div>
              <div className="ml-2 mt-1 text-xs text-muted-foreground italic">
                Add NP (New Patient) or EP (Established Patient) prefix based on encounter history
              </div>
            </div>

            <div>
              <div className="font-medium">Message</div>
              <div className="ml-2 bg-muted/50 p-3 rounded">
                <div className="text-sm whitespace-pre-wrap font-mono">{teMessage}</div>
              </div>
            </div>
          </div>
        </div>

        {isUrgent && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ Scheduling priority is {referral.schedulingPriority}. Mark the Telephone Encounter as High Priority in
              eCW.
            </AlertDescription>
          </Alert>
        )}

        {isSleepDiagnosis && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Sleep-related diagnosis — Use Eslin Luka as rendering provider.
            </AlertDescription>
          </Alert>
        )}

        {isSleepDiagnosis && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Sleep-related diagnosis — Assign referral document and Telephone Encounter to Nicole Dunlap.
            </AlertDescription>
          </Alert>
        )}

        {hasProviderConflict && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ {renderingProviderName} doesn&apos;t typically accept this diagnosis per the scheduling guide.
              Consider: {alternativeProviderNames.join(", ")}.
            </AlertDescription>
          </Alert>
        )}

        {needsHmoReferral && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              ⚠️ Patient Needs PCP/HMO Referral. Please contact referring provider.
            </AlertDescription>
          </Alert>
        )}

        {isOptumServe && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              Optum Serve referral; only Gincy Samuel, MD accepts these referrals
            </AlertDescription>
          </Alert>
        )}

        {missingInfo.length > 0 && (
          <Alert className="mt-2">
            <AlertDescription className="text-sm">
              <div>
                ℹ️ Requested the following - please check if they&apos;ve arrived before reaching out to the provider:
              </div>
              <ul className="list-disc list-inside space-y-1 mt-2">
                {missingInfo.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>

              {outboundFaxFile && (
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm" className="mt-3">
                      View Outbound Fax
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl h-[80vh]">
                    <DialogHeader>
                      <DialogTitle>Outbound Fax - Missing Information Request</DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden">
                      <PDFViewer url={fileViewUrl(outboundFaxFile.pathToFile)} />
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}

function AutonomyReferralCards({ referral }: Pick<AutonomyReferralInstructionsProps, "referral">) {
  return (
    <>
      <PatientCard patient={referral.patient} />
      <ReferralProviderCards referral={referral} />
      <ReferralCard referral={referral} isEditable />
      {referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable />}
      {referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable />}
    </>
  );
}

export function AutonomyReferralInstructions({
  referral,
  task,
  faxSenderNumber = null,
}: AutonomyReferralInstructionsProps) {
  return (
    <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">
      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <AutonomyReferralMessage referral={referral} task={task} faxSenderNumber={faxSenderNumber} />
        </div>
      </EhrInstructionsCard>

      <AutonomyReferralCards referral={referral} />
    </div>
  );
}
